import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { LumberProfileType } from "building-types/framing/lumberTypes";
import { SettingsType } from "building-types/settings/settingsType";
import { ExteriorWallOffsetMode } from "building-types/settings/settingsTypesEnum";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { WallMaterialType } from "building-types/walls/wallMaterialTypesEnum";
import type { WallConditionData, WallType, WallTypeTable, WallConditionTable } from "building-types/walls/wallType";
import { WallCondition } from "building-types/walls/wallType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { wallMaterialIdFromLumberProfile, defaultWallMaterialIdByType } from "./wallMaterialTools";

const REFERENCE_INTERIOR_WALL_CONDITION = WallCondition.INTERIOR_INTERIOR;
const REFERENCE_INTERIOR_ROOF_WALL_CONDITION = WallCondition.INTERIOR_ROOF;

export const isExteriorWallCondition: {
  [key in WallCondition]?: boolean;
} = {
  [WallCondition.EXTERIOR_INTERIOR]: true,
  [WallCondition.EXTERIOR_GARAGE]: true,
  [WallCondition.EXTERIOR_PLUMBING]: true,
  [WallCondition.EXTERIOR_DORMER]: true,
  [WallCondition.EXTERIOR_GABLE_END]: true,
  [WallCondition.EXTERIOR_CRIPPLE]: true,
};

export const isFoundationWallCondition: {
  [key in WallCondition]?: boolean;
} = {
  [WallCondition.EXTERIOR_CRIPPLE]: true,
  [WallCondition.INTERIOR_FURRING]: true,
};

export function wallConditionDataNew(wallTypeId: string): WallConditionData {
  return {
    wallTypeId,
  };
}

export function wallTypeNew(
  name: string,
  structuralMaterialId: string,
  exteriorSubstrateMaterialId?: string,
): WallType {
  return {
    catalogItemType: "WALL_TYPE",
    name,
    structuralMaterialId,
    exteriorSubstrateMaterialId,
  };
}

export const defaultInteriorWallId = "WALL-INT-STD";
const defaultInteriorWallType = wallTypeNew(
  defaultInteriorWallId,
  wallMaterialIdFromLumberProfile[LumberProfileType.SPF_2X4],
);

export const defaultExteriorWallId = "WALL-EXT-STD";
const defaultExteriorWallType = wallTypeNew(
  defaultExteriorWallId,
  wallMaterialIdFromLumberProfile[LumberProfileType.SPF_2X4],
  defaultWallMaterialIdByType[WallMaterialType.SUBSTRATE],
);

export const defaultWallTypes: WallTypeTable = {
  [defaultInteriorWallId]: defaultInteriorWallType,
  [defaultExteriorWallId]: defaultExteriorWallType,
};

export const defaultWallConditions: WallConditionTable = {
  [WallCondition.EXTERIOR_INTERIOR]: wallConditionDataNew(defaultExteriorWallId),
  [WallCondition.EXTERIOR_GARAGE]: wallConditionDataNew(defaultExteriorWallId),
  [WallCondition.EXTERIOR_PLUMBING]: wallConditionDataNew(defaultExteriorWallId),
  [WallCondition.EXTERIOR_DORMER]: wallConditionDataNew(defaultExteriorWallId),
  [WallCondition.EXTERIOR_GABLE_END]: wallConditionDataNew(defaultExteriorWallId),
  [WallCondition.EXTERIOR_CRIPPLE]: wallConditionDataNew(defaultExteriorWallId),
  [WallCondition.INTERIOR_INTERIOR]: wallConditionDataNew(defaultInteriorWallId),
  [WallCondition.INTERIOR_GARAGE]: wallConditionDataNew(defaultInteriorWallId),
  [WallCondition.INTERIOR_PLUMBING]: wallConditionDataNew(defaultInteriorWallId),
  [WallCondition.INTERIOR_ROOF]: wallConditionDataNew(defaultInteriorWallId),
  [WallCondition.INTERIOR_FURRING]: wallConditionDataNew(defaultInteriorWallId),
};
function getDefaultWallTypeByWallCondition(wallCondition: WallCondition): WallType {
  return isExteriorWallCondition[wallCondition] ? defaultExteriorWallType : defaultInteriorWallType;
}

export function getWallTypeByWallCondition(
  wallConditions: WallConditionTable,
  wallTypes: WallTypeTable,
  wallCondition: WallCondition,
): WallType {
  const wallTypeId = wallConditions[wallCondition]?.wallTypeId;

  if (!wallTypeId) {
    return getDefaultWallTypeByWallCondition(wallCondition);
  }

  const wallType = wallTypes[wallTypeId];

  if (!wallType) {
    return getDefaultWallTypeByWallCondition(wallCondition);
  }

  return wallType;
}
function getWallTypeLayerThickness(
  wallType: WallType,
  wallCatalog: WallCatalogSettings,
  wallLayers: {
    [K in keyof Omit<WallType, "name">]?: true;
  },
): number {
  let thickness = 0;
  for (const layer in wallLayers) {
    thickness += wallCatalog.wallMaterials[wallType[layer]]?.thickness ?? 0;
  }
  return thickness;
}

export function getExteriorWallOffset(
  wallSettings: WallSettings,
  overhangDatum: WallLateralDatumAnchor = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
) {
  switch (overhangDatum) {
    case WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING:
      return wallSettings.exteriorWallOffset;
    case WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE:
      return wallSettings.exteriorWallOffset + wallSettings.exteriorWallSubstrateThickness;
    default:
      assertUnreachable(overhangDatum);
  }
}

export function getExteriorWallSubstrateOffset(wallSettings: WallSettings) {
  return wallSettings.exteriorWallOffset + wallSettings.exteriorWallSubstrateThickness;
}

export function getExteriorWallStructualOffset(
  exteriorWallStructuralThickness: number,
  exteriorWallCladdingThickness: number,
  baseInteriorWallThickness: number,
  exteriorWallOffsetMode: ExteriorWallOffsetMode,
  exteriorWallOffsetDatum: WallLateralDatumAnchor,
  outsideCornerWallOffsetForInteriorWallThickening: number,
): number {
  let exteriorWallStructuralOffset: number;
  switch (exteriorWallOffsetMode) {
    case ExteriorWallOffsetMode.INSIDE_THICKENING_ALL:
    case ExteriorWallOffsetMode.INSIDE_THICKENING: {
      exteriorWallStructuralOffset = outsideCornerWallOffsetForInteriorWallThickening;

      if (exteriorWallOffsetDatum === WallLateralDatumAnchor.OUTSIDE_FACE_OF_SUBSTRATE) {
        exteriorWallStructuralOffset -= exteriorWallCladdingThickness;
      }

      break;
    }
    case ExteriorWallOffsetMode.OUTSIDE_THICKENING: {
      exteriorWallStructuralOffset = getExteriorWallOutsideThickeningOffset(
        exteriorWallStructuralThickness,
        baseInteriorWallThickness,
      );
      break;
    }
  }

  return exteriorWallStructuralOffset;
}
function getExteriorWallOutsideThickeningOffset(
  exteriorWallThickness: number,
  baseInteriorWallThickness: number,
): number {
  return exteriorWallThickness - baseInteriorWallThickness / 2;
}

export const REFERENCE_EXTERIOR_WALL_CONDITION = WallCondition.EXTERIOR_INTERIOR;

export function computeWallSettingsOffsets(
  wallSettings: WallSettings,
  wallCatalogSettings: WallCatalogSettings,
): WallSettings {
  const {
    exteriorWallMaterial,
    exteriorWallOffsetMode,
    exteriorWallOffsetDatum,
    wallConditions,
    outsideCornerWallOffsetForInteriorWallThickening,
  } = wallSettings;
  const { wallTypes } = wallCatalogSettings;

  const refInteriorWallType = getWallTypeByWallCondition(wallConditions, wallTypes, REFERENCE_INTERIOR_WALL_CONDITION);
  const refInteriorRoofWallType = getWallTypeByWallCondition(
    wallConditions,
    wallTypes,
    REFERENCE_INTERIOR_ROOF_WALL_CONDITION,
  );
  const refExteriorWallType = getWallTypeByWallCondition(wallConditions, wallTypes, REFERENCE_EXTERIOR_WALL_CONDITION);

  const interiorWallThickness = getWallTypeLayerThickness(refInteriorWallType, wallCatalogSettings, {
    structuralMaterialId: true,
  });
  const interiorRoofWallThickness = getWallTypeLayerThickness(refInteriorRoofWallType, wallCatalogSettings, {
    structuralMaterialId: true,
  });
  const exteriorWallStructuralThickness = getWallTypeLayerThickness(refExteriorWallType, wallCatalogSettings, {
    structuralMaterialId: true,
  });
  const exteriorWallSubstrateThickness = getWallTypeLayerThickness(refExteriorWallType, wallCatalogSettings, {
    exteriorSubstrateMaterialId: true,
  });

  const exteriorWallOffset = getExteriorWallStructualOffset(
    exteriorWallStructuralThickness,
    exteriorWallSubstrateThickness,
    interiorWallThickness,
    exteriorWallOffsetMode,
    exteriorWallOffsetDatum,
    outsideCornerWallOffsetForInteriorWallThickening,
  );

  const insideCornerWallOffsetForInteriorWallThickening = getExteriorWallOutsideThickeningOffset(
    exteriorWallStructuralThickness,
    interiorWallThickness,
  );
  const insideCornerWallOffsetForInteriorRoofWallThickening = getExteriorWallOutsideThickeningOffset(
    interiorRoofWallThickness,
    interiorWallThickness,
  );

  const interiorWallOffset = interiorWallThickness / 2;
  const exteriorWallBaseThickness = exteriorWallStructuralThickness + exteriorWallSubstrateThickness;

  return {
    type: SettingsType.WALL_SETTINGS,
    exteriorWallOffset,
    interiorWallOffset,
    exteriorWallMaterial,
    exteriorWallOffsetMode,
    exteriorWallOffsetDatum,
    exteriorWallBaseThickness,
    exteriorWallSubstrateThickness,
    wallConditions,
    insideCornerWallOffsetForInteriorWallThickening,
    insideCornerWallOffsetForInteriorRoofWallThickening,
    outsideCornerWallOffsetForInteriorWallThickening,
  };
}
